import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { confirmToast, errorToast, successToast } from '@/common/toast';
import { Container, DataGrid, SectionHeader } from '@/components/common';
import { DateCell } from '@/components/pages/components';
import { useAssignmentsByCourse, useDeleteAssignment } from '@/hooks/assignments.hook';
import { useCourseDetail } from '@/hooks/courses.hook';
import AssignmentAddModal from './components/AssignmentAddModal';
import AssignmentEditModal from './components/AssignmentEditModal';

/**
 * 과제 목록 페이지 컴포넌트
 * 
 * 특정 과정의 과제 목록을 조회하고 관리하는 페이지입니다.
 */
const AssignmentListPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  // 모달 상태 관리
  const [addAssignmentModalOpen, setAddAssignmentModalOpen] = useState(false);
  const [editAssignmentModalOpen, setEditAssignmentModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // 과정 정보 조회
  const { data: courseData } = useCourseDetail(courseId);

  // 과제 목록 조회 (페이징 없음, List 반환)
  const { data: assignmentList = [], isLoading, refetch } = useAssignmentsByCourse(
    parseInt(courseId)
  );

  // 과제 삭제 mutation
  const deleteAssignmentMutation = useDeleteAssignment();

  // 이미 과제가 있는 videoId 목록 추출
  const existingVideoIds = useMemo(() => {
    return assignmentList
      .map(assignment => assignment.courseVideo?.videoId)
      .filter(videoId => videoId !== undefined && videoId !== null);
  }, [assignmentList]);

  /**
   * 과제 삭제 핸들러
   */
  const handleDeleteAssignment = useCallback((assignment) => {
    confirmToast(
      `"${assignment.title}" 과제를 삭제하시겠습니까?\n관련된 제출물과 댓글도 함께 삭제됩니다.`,
      async () => {
        try {
          await deleteAssignmentMutation.mutateAsync(assignment.assignmentId);
          successToast('과제가 삭제되었습니다.');
          refetch();
        } catch (error) {
          errorToast(`과제 삭제에 실패했습니다. - ${error.response?.data?.msg}`);
        }
      }
    );
  }, [deleteAssignmentMutation, refetch]);

  /**
   * 과제 수정 핸들러
   */
  const handleEditAssignment = useCallback((assignment) => {
    setSelectedAssignment(assignment);
    setEditAssignmentModalOpen(true);
  }, []);

  /**
   * 과제 추가 핸들러
   */
  const handleAddAssignment = useCallback(() => {
    setAddAssignmentModalOpen(true);
  }, []);

  // 과제 컬럼 정의
  const assignmentColumns = useMemo(() => [
    {
      field: 'assignmentId',
      headerName: '과제 ID',
      flex: 9,
      minWidth: 90,
      sortable: false,
      type: 'number',
    },
    {
      field: 'courseVideo',
      headerName: '해당 강의',
      flex: 25,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}
          >
            {params.row.courseVideo?.videoName || '-'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'title',
      headerName: '과제명',
      flex: 25,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'description',
      headerName: '설명',
      flex: 60,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}
          >
            {params.row.description || '-'}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'attachedFileUrl',
      headerName: '참고 링크',
      flex: 10,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const hasLink = params.row.attachedFileUrl;
        if (hasLink) {
          let url = params.row.attachedFileUrl.trim();
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = `https://${url}`;
          }
          return (
            <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
              <Tooltip title="새 창에서 열기">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
      },
    },
    {
      field: 'createdAt',
      headerName: '등록일',
      flex: 12,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="createdAt" />,
    },
    {
      field: 'actions',
      headerName: '작업',
      flex: 10,
      minWidth: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="수정">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditAssignment(params.row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteAssignment(params.row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [handleEditAssignment, handleDeleteAssignment]);

  return (
    <Container title={`과제 목록${courseData ? ` - ${courseData.courseName}` : ''}`}>
      {/* 상단 네비게이션 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          뒤로 가기
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* 상단: 과제 목록 */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <SectionHeader sx={{ margin: 0, padding: 0, border: 'none' }}>
                  <PlayIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={600}>
                    과제 목록
                  </Typography>
                </SectionHeader>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAssignment}
                >
                  과제 추가
                </Button>
              </Stack>

              <DataGrid
                rows={assignmentList}
                columns={assignmentColumns}
                loading={isLoading}
                getRowId={(row) => row.assignmentId}
                pagination={false}
                height={'auto'}
                noRowsMessage="등록된 과제가 없습니다."
                onCellClick={(params, event) => {
                  if (params.field === 'actions') {
                    event.stopPropagation();
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 과제 추가 모달 */}
      <AssignmentAddModal
        open={addAssignmentModalOpen}
        onClose={() => setAddAssignmentModalOpen(false)}
        courseId={parseInt(courseId)}
        existingVideoIds={existingVideoIds}
        onSuccess={() => {
          refetch();
        }}
      />

      {/* 과제 수정 모달 */}
      <AssignmentEditModal
        open={editAssignmentModalOpen}
        onClose={() => {
          setEditAssignmentModalOpen(false);
          setSelectedAssignment(null);
        }}
        assignment={selectedAssignment}
        onSuccess={() => {
          refetch();
        }}
      />
    </Container>
  );
};

export default AssignmentListPage;
